import { memo, ReactNode, useCallback, useContext, useEffect } from "react";
import { flushSync } from "react-dom";
import type { Blocker, History, Transition } from "history";
import { UNSAFE_NavigationContext } from "react-router";
import { useConfirmation } from "./Confirmation";

function useBlocker(blocker: Blocker, when = true): void {
  const navigationContextObject = useContext(UNSAFE_NavigationContext);

  const navigator = navigationContextObject
    .navigator as History;

  useEffect(() => {
    if (!when) return;

    const unblock = navigator.block((tx: Transition) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          // Automatically unblock the transition so it can play all the way
          // through before retrying it. TODO: Figure out how to re-enable
          // this block if the transition is cancelled for some reason.
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [ navigator, blocker, when ]);
}

export type NavigationConfirmationProps = {
  title?: string,
  text?: ReactNode,
  enabled: boolean,
  onConfirm?: () => void,
  onCancel?: () => void,
}

export const NavigationConfirmation = memo<NavigationConfirmationProps>((
  {
    enabled,
    title = "Cancel changes",
    text = "All change will be lost. Do you want to continue?",
    ...props
  }) => {

  const { push } = useConfirmation();

  const blocker: Blocker = useCallback(
    (tx) => {
      push({
        confirmationId: "NavigationConfirmation",
        title,
        children: text,
        onSubmit: (result: boolean) => {
          if (result) {
            flushSync(() => props?.onConfirm?.());
            tx.retry();
          } else {
            flushSync(() => props?.onCancel?.());
          }
          return true;
        },
      });
    },
    [ title, text, props, push ],
  );

  useBlocker(blocker, enabled);
  return null;
});

export default NavigationConfirmation;
